import React, { useState } from "react";
import { Check, Download, Share2, ExternalLink } from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

const personalActionItems = [
  {
    id: 1,
    category: "Home",
    title: "Switch to LED bulbs",
    impact: "Medium",
    description:
      "Replace conventional bulbs with LED alternatives which use up to 85% less energy and last 25 times longer.",
    details:
      "A typical home switching all bulbs to LEDs can save about 250kg of CO₂ emissions annually.",
  },
  {
    id: 2,
    category: "Transportation",
    title: "Bike or walk for short trips",
    impact: "High",
    description:
      "Replace car journeys under 2 miles with walking or cycling when possible.",
    details:
      "If you replace 5 miles of driving per week with cycling, you could reduce your carbon footprint by about 170kg of CO₂ per year.",
  },
  {
    id: 3,
    category: "Food",
    title: "Eat one vegetarian meal per day",
    impact: "High",
    description:
      "Replace one meat-based meal with a plant-based alternative daily.",
    details:
      "Switching one meat meal to vegetarian each day can reduce your food carbon footprint by up to 30%.",
  },
  {
    id: 4,
    category: "Energy",
    title: "Wash clothes in cold water",
    impact: "Medium",
    description:
      "Use cold water for laundry to save energy used for heating water.",
    details:
      "Washing clothes in cold water can reduce the associated carbon emissions by up to 90% compared to using hot water.",
  },
  {
    id: 5,
    category: "Consumption",
    title: "Buy second-hand when possible",
    impact: "Medium",
    description:
      "Purchase pre-owned items instead of new to extend product lifecycles.",
    details:
      "Buying a used item instead of new can avoid the emissions associated with manufacturing and shipping new products.",
  },
];

const communityActions = [
  {
    id: 1,
    name: "Climate Reality Project",
    type: "Education and Advocacy",
    website: "https://www.climaterealityproject.org/",
    description:
      "Join a network of climate advocates working to implement practical solutions across the globe.",
  },
  {
    id: 2,
    name: "350.org",
    type: "Grassroots Campaigns",
    website: "https://350.org/",
    description:
      "A global movement working to end the use of fossil fuels and build renewable energy for all.",
  },
  {
    id: 3,
    name: "Citizens Climate Lobby",
    type: "Political Action",
    website: "https://citizensclimatelobby.org/",
    description:
      "A non-profit organization that trains and supports volunteers to build relationships with elected officials to influence climate policy.",
  },
];

const policyActions = [
  {
    id: 1,
    title: "Clean Energy Petition",
    organization: "Sierra Club",
    website: "https://www.sierraclub.org/",
    description:
      "Urge your representatives to support the transition to 100% clean, renewable energy through this petition.",
  },
  {
    id: 2,
    title: "Carbon Pricing Advocacy",
    organization: "Citizens Climate Lobby",
    website: "https://citizensclimatelobby.org/",
    description:
      "Support the implementation of carbon pricing policies that incentivize renewable energy adoption.",
  },
];

const ActionToolkit = () => {
  const [activeTab, setActiveTab] = useState("personal");
  const [selectedItems, setSelectedItems] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);

  const toggleItemSelection = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((itemId) => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleDownloadChecklist = () => {
    // In a real implementation, this would generate and download a PDF
    alert(
      "In a complete implementation, this would download a personalized PDF checklist of your selected actions."
    );
  };

  const handleShare = () => {
    // In a real implementation, this would open social sharing options
    if (navigator.share) {
      navigator
        .share({
          title: "My Carbon Action Plan",
          text: "Check out my carbon reduction plan from the Carbon Literacy Course!",
          url: window.location.href,
        })
        .catch((error) => console.log("Error sharing", error));
    } else {
      alert(
        "Web Share API not supported in your browser. In a complete implementation, this would show social share options."
      );
    }
  };

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  const renderPersonalActions = () => {
    return (
      <div>
        <div className="mb-6 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">
            Personal Action Checklist
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadChecklist}
              disabled={selectedItems.length === 0}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm ${
                selectedItems.length > 0
                  ? "bg-primary-500 text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Download className="w-4 h-4 mr-1" />
              Download PDF
            </button>
            <button
              onClick={handleShare}
              className="flex items-center px-3 py-1.5 bg-edu-blue text-white rounded-md text-sm"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Select
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Category
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Action
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Impact
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {personalActionItems.map((item) => (
                <tr
                  key={item.id}
                  className={
                    selectedItems.includes(item.id) ? "bg-primary-100" : ""
                  }
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleItemSelection(item.id)}
                        className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.category}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {item.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.impact === "High"
                          ? "bg-green-100 text-green-800"
                          : item.impact === "Medium"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {item.impact}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {item.details}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCommunityActions = () => {
    return (
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-6">
          Community & Organizational Involvement
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {communityActions.map((org) => (
            <div
              key={org.id}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="bg-primary-700 py-2 px-4">
                <h4 className="text-lg font-medium text-white">{org.name}</h4>
              </div>
              <div className="p-4">
                <div className="text-sm font-medium text-primary-500 mb-2">
                  {org.type}
                </div>
                <p className="text-sm text-gray-600 mb-4">{org.description}</p>
                <a
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-edu-blue hover:text-edu-blue-dark text-sm"
                >
                  Visit Website
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-primary-100 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-gray-800 mb-3">
            Local Engagement Tips
          </h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Attend town halls and council meetings to advocate for climate
                policies
              </span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Join or form a neighborhood sustainability group
              </span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Volunteer for local environmental cleanup or tree planting
                events
              </span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Support businesses with sustainable practices and provide
                feedback to those without
              </span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const renderPolicyActions = () => {
    return (
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-6">
          Policy & Advocacy Support
        </h3>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h4 className="text-lg font-medium text-gray-800 mb-4">
            Current Petitions & Campaigns
          </h4>

          <div className="space-y-6">
            {policyActions.map((action) => (
              <div
                key={action.id}
                className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="text-base font-medium text-gray-900">
                      {action.title}
                    </h5>
                    <p className="text-sm text-gray-500 mb-2">
                      Organized by: {action.organization}
                    </p>
                    <p className="text-sm text-gray-600">
                      {action.description}
                    </p>
                  </div>
                  <a
                    href={action.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-edu-blue text-white px-3 py-1 rounded-md text-sm flex items-center"
                  >
                    Support
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-primary-100 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-gray-800 mb-3">
            How to Contact Representatives
          </h4>
          <p className="text-gray-700 mb-4">
            Your voice matters. Here's how you can effectively communicate with
            elected officials about climate issues:
          </p>

          <ol className="list-decimal pl-5 space-y-2 text-gray-700">
            <li>
              Find your representatives at all levels of government (local,
              state, federal)
            </li>
            <li>
              Personalize your message with specific concerns and requests
            </li>
            <li>Reference specific bills or policies when applicable</li>
            <li>Be respectful and concise</li>
            <li>Follow up if you don't receive a response</li>
          </ol>

          <div className="mt-4">
            <a
              href="https://www.usa.gov/elected-officials"
              target="_blank"
              rel="noopener noreferrer"
              className="text-edu-blue hover:underline inline-flex items-center"
            >
              Find your elected officials
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Action Toolkit<span className="text-edu-orange">→</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      <div className="bg-primary-100 p-6 rounded-lg mb-12">
        <p className="text-lg text-gray-800">
          Knowledge is only the first step—action is where change happens. This
          toolkit provides practical ways to reduce your carbon footprint,
          engage with your community, and advocate for climate-friendly
          policies.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-12">
        <button
          className={`px-6 py-3 ${
            activeTab === "personal"
              ? "border-b-2 border-primary-500 text-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("personal")}
        >
          Personal Actions
        </button>
        <button
          className={`px-6 py-3 ${
            activeTab === "community"
              ? "border-b-2 border-primary-500 text-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("community")}
        >
          Community Engagement
        </button>
        <button
          className={`px-6 py-3 ${
            activeTab === "policy"
              ? "border-b-2 border-primary-500 text-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("policy")}
        >
          Policy Advocacy
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="mb-16">
        {activeTab === "personal" && renderPersonalActions()}
        {activeTab === "community" && renderCommunityActions()}
        {activeTab === "policy" && renderPolicyActions()}
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/quiz"
        prevText="Previous: Quiz"
        nextLink="/further-learning"
        nextText="Next: Further Learning"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default ActionToolkit;
